/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	ClearDeviceVerification400,
	ClearDeviceVerification404,
	ClearDeviceVerification500,
	ClearDeviceWarnings400,
	ClearDeviceWarnings404,
	ClearDeviceWarnings500,
	CreateDevice400,
	CreateDevice401,
	CreateDevice403,
	CreateDevice409,
	CreateDevice500,
	DeleteDevice400,
	DeleteDevice404,
	DeleteDevice500,
	Device,
	DeviceCreate,
	DeviceUpdate,
	GetDevice400,
	GetDevice404,
	GetDevice500,
	GetDeviceDebugInfo400,
	GetDeviceDebugInfo404,
	GetDeviceDebugInfo500,
	ListDevices400,
	ListDevices500,
	RunDeviceNetworkTest400,
	RunDeviceNetworkTest404,
	RunDeviceNetworkTest429,
	RunDeviceNetworkTest500,
	SetDeviceOperatingMode400,
	SetDeviceOperatingMode404,
	SetDeviceOperatingMode500,
	SetDeviceOperatingModeBody,
	UpdateDevice400,
	UpdateDevice403,
	UpdateDevice404,
	UpdateDevice500,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getListDevicesUrl = () => {
	return `/devices`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary List all devices owned by the authenticated user
 */
export const listDevices = async (
	options?: Parameters<typeof customFetch>[1],
): Promise<Device[]> => {
	return customFetch<Device[]>(getListDevicesUrl(), {
		...options,
		method: "GET",
	});
};

export const getListDevicesQueryKey = () => {
	return ["devices"] as const;
};

export const getListDevicesQueryOptions = <
	TData = Awaited<ReturnType<typeof listDevices>>,
	TError = ListDevices400 | void | ListDevices500,
>(options?: {
	query?: Partial<
		UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>
	>;
	request?: SecondParameter<typeof customFetch>;
}) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getListDevicesQueryKey();

	const queryFn: QueryFunction<Awaited<ReturnType<typeof listDevices>>> = ({
		signal,
	}) => listDevices({ signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listDevices>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListDevicesQueryResult = NonNullable<
	Awaited<ReturnType<typeof listDevices>>
>;
export type ListDevicesQueryError = ListDevices400 | void | ListDevices500;

export function useListDevices<
	TData = Awaited<ReturnType<typeof listDevices>>,
	TError = ListDevices400 | void | ListDevices500,
>(
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listDevices>>,
					TError,
					Awaited<ReturnType<typeof listDevices>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListDevices<
	TData = Awaited<ReturnType<typeof listDevices>>,
	TError = ListDevices400 | void | ListDevices500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listDevices>>,
					TError,
					Awaited<ReturnType<typeof listDevices>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListDevices<
	TData = Awaited<ReturnType<typeof listDevices>>,
	TError = ListDevices400 | void | ListDevices500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List all devices owned by the authenticated user
 */

export function useListDevices<
	TData = Awaited<ReturnType<typeof listDevices>>,
	TError = ListDevices400 | void | ListDevices500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getListDevicesQueryOptions(options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List all devices owned by the authenticated user
 */
export const useSetListDevicesQueryData = () => {
	const queryClient = useQueryClient();
	return (
		updater:
			| Awaited<ReturnType<typeof listDevices>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof listDevices>> | undefined,
			  ) => Awaited<ReturnType<typeof listDevices>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof listDevices>>>(
			{ exact: $exactMatch, queryKey: getListDevicesQueryKey() },
			updater,
		);
	};
};

/**
 * @summary List all devices owned by the authenticated user
 */
export const useGetListDevicesQueryData = () => {
	const queryClient = useQueryClient();
	return () =>
		queryClient.getQueryData<Awaited<ReturnType<typeof listDevices>>>(
			getListDevicesQueryKey(),
		);
};

export const getCreateDeviceUrl = () => {
	return `/devices`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Register a new first-party device
 */
export const createDevice = async (
	deviceCreate: DeviceCreate,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<Device>(getCreateDeviceUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(deviceCreate),
	});
};

export const getCreateDeviceMutationKey = () => ["createDevice"] as const;

export const getCreateDeviceMutationOptions = <
	TError =
		| CreateDevice400
		| CreateDevice401
		| CreateDevice403
		| CreateDevice409
		| CreateDevice500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createDevice>>,
		TError,
		CreateDeviceMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createDevice>>,
	TError,
	CreateDeviceMutationVariables,
	TContext
> => {
	const mutationKey = getCreateDeviceMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createDevice>>,
		CreateDeviceMutationVariables
	> = (props) => {
		const { data } = props ?? {};

		return createDevice(data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateDeviceMutationResult = NonNullable<
	Awaited<ReturnType<typeof createDevice>>
>;
export type CreateDeviceMutationBody = DeviceCreate;
export type CreateDeviceMutationError =
	| CreateDevice400
	| CreateDevice401
	| CreateDevice403
	| CreateDevice409
	| CreateDevice500;
export type CreateDeviceMutationVariables = { data: DeviceCreate };

/**
 * @summary Register a new first-party device
 */
export const useCreateDevice = <
	TError =
		| CreateDevice400
		| CreateDevice401
		| CreateDevice403
		| CreateDevice409
		| CreateDevice500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof createDevice>>,
			TError,
			CreateDeviceMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof createDevice>>,
	TError,
	CreateDeviceMutationVariables,
	TContext
> => {
	return useMutation(getCreateDeviceMutationOptions(options), queryClient);
};
export const getGetDeviceUrl = (deviceId: string) => {
	return `/devices/${deviceId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Get a single device by ID
 */
export const getDevice = async (
	deviceId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	return customFetch<Device>(getGetDeviceUrl(deviceId), {
		...options,
		method: "GET",
	});
};

export const getGetDeviceQueryKey = (deviceId: string) => {
	return ["devices", deviceId] as const;
};

export const getGetDeviceQueryOptions = <
	TData = Awaited<ReturnType<typeof getDevice>>,
	TError = GetDevice400 | void | GetDevice404 | GetDevice500,
>(
	deviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getGetDeviceQueryKey(deviceId);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof getDevice>>> = ({
		signal,
	}) => getDevice(deviceId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: deviceId !== null && deviceId !== undefined,
		...queryOptions,
	} as UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData> & {
		queryKey: DataTag<QueryKey, TData, TError>;
	};
};

export type GetDeviceQueryResult = NonNullable<
	Awaited<ReturnType<typeof getDevice>>
>;
export type GetDeviceQueryError =
	| GetDevice400
	| void
	| GetDevice404
	| GetDevice500;

export function useGetDevice<
	TData = Awaited<ReturnType<typeof getDevice>>,
	TError = GetDevice400 | void | GetDevice404 | GetDevice500,
>(
	deviceId: string,
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getDevice>>,
					TError,
					Awaited<ReturnType<typeof getDevice>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDevice<
	TData = Awaited<ReturnType<typeof getDevice>>,
	TError = GetDevice400 | void | GetDevice404 | GetDevice500,
>(
	deviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getDevice>>,
					TError,
					Awaited<ReturnType<typeof getDevice>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDevice<
	TData = Awaited<ReturnType<typeof getDevice>>,
	TError = GetDevice400 | void | GetDevice404 | GetDevice500,
>(
	deviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get a single device by ID
 */

export function useGetDevice<
	TData = Awaited<ReturnType<typeof getDevice>>,
	TError = GetDevice400 | void | GetDevice404 | GetDevice500,
>(
	deviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetDeviceQueryOptions(deviceId, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get a single device by ID
 */
export const useSetGetDeviceQueryData = () => {
	const queryClient = useQueryClient();
	return (
		deviceId: string,
		updater:
			| Awaited<ReturnType<typeof getDevice>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getDevice>> | undefined,
			  ) => Awaited<ReturnType<typeof getDevice>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getDevice>>>(
			{ exact: $exactMatch, queryKey: getGetDeviceQueryKey(deviceId) },
			updater,
		);
	};
};

/**
 * @summary Get a single device by ID
 */
export const useGetGetDeviceQueryData = () => {
	const queryClient = useQueryClient();
	return (deviceId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getDevice>>>(
			getGetDeviceQueryKey(deviceId),
		);
};

export const getUpdateDeviceUrl = (deviceId: string) => {
	return `/devices/${deviceId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Update an existing device
 */
export const updateDevice = async (
	deviceId: string,
	deviceUpdate: DeviceUpdate,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<Device>(getUpdateDeviceUrl(deviceId), {
		...options,
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(deviceUpdate),
	});
};

export const getUpdateDeviceMutationKey = () => ["updateDevice"] as const;

export const getUpdateDeviceMutationOptions = <
	TError =
		| UpdateDevice400
		| void
		| UpdateDevice403
		| UpdateDevice404
		| UpdateDevice500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof updateDevice>>,
		TError,
		UpdateDeviceMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof updateDevice>>,
	TError,
	UpdateDeviceMutationVariables,
	TContext
> => {
	const mutationKey = getUpdateDeviceMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof updateDevice>>,
		UpdateDeviceMutationVariables
	> = (props) => {
		const { deviceId, data } = props ?? {};

		return updateDevice(deviceId, data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type UpdateDeviceMutationResult = NonNullable<
	Awaited<ReturnType<typeof updateDevice>>
>;
export type UpdateDeviceMutationBody = DeviceUpdate;
export type UpdateDeviceMutationError =
	| UpdateDevice400
	| void
	| UpdateDevice403
	| UpdateDevice404
	| UpdateDevice500;
export type UpdateDeviceMutationVariables = {
	deviceId: string;
	data: DeviceUpdate;
};

/**
 * @summary Update an existing device
 */
export const useUpdateDevice = <
	TError =
		| UpdateDevice400
		| void
		| UpdateDevice403
		| UpdateDevice404
		| UpdateDevice500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof updateDevice>>,
			TError,
			UpdateDeviceMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof updateDevice>>,
	TError,
	UpdateDeviceMutationVariables,
	TContext
> => {
	return useMutation(getUpdateDeviceMutationOptions(options), queryClient);
};
export const getDeleteDeviceUrl = (deviceId: string) => {
	return `/devices/${deviceId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Delete a device and close its sensor fragments
 */
export const deleteDevice = async (
	deviceId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	return customFetch<Device>(getDeleteDeviceUrl(deviceId), {
		...options,
		method: "DELETE",
	});
};

export const getDeleteDeviceMutationKey = () => ["deleteDevice"] as const;

export const getDeleteDeviceMutationOptions = <
	TError = DeleteDevice400 | void | DeleteDevice404 | DeleteDevice500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof deleteDevice>>,
		TError,
		DeleteDeviceMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof deleteDevice>>,
	TError,
	DeleteDeviceMutationVariables,
	TContext
> => {
	const mutationKey = getDeleteDeviceMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof deleteDevice>>,
		DeleteDeviceMutationVariables
	> = (props) => {
		const { deviceId } = props ?? {};

		return deleteDevice(deviceId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type DeleteDeviceMutationResult = NonNullable<
	Awaited<ReturnType<typeof deleteDevice>>
>;

export type DeleteDeviceMutationError =
	| DeleteDevice400
	| void
	| DeleteDevice404
	| DeleteDevice500;
export type DeleteDeviceMutationVariables = { deviceId: string };

/**
 * @summary Delete a device and close its sensor fragments
 */
export const useDeleteDevice = <
	TError = DeleteDevice400 | void | DeleteDevice404 | DeleteDevice500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof deleteDevice>>,
			TError,
			DeleteDeviceMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof deleteDevice>>,
	TError,
	DeleteDeviceMutationVariables,
	TContext
> => {
	return useMutation(getDeleteDeviceMutationOptions(options), queryClient);
};
export const getGetDeviceDebugInfoUrl = (deviceId: string) => {
	return `/devices/${deviceId}/debug-info`;
};

/**
 * **Access:** Requires a user session token. Administrators only.
 * @summary Get debug information for a device (admin only)
 */
export const getDeviceDebugInfo = async (
	deviceId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	return customFetch<Device>(getGetDeviceDebugInfoUrl(deviceId), {
		...options,
		method: "GET",
	});
};

export const getGetDeviceDebugInfoQueryKey = (deviceId: string) => {
	return ["devices", deviceId, "debug-info"] as const;
};

export const getGetDeviceDebugInfoQueryOptions = <
	TData = Awaited<ReturnType<typeof getDeviceDebugInfo>>,
	TError =
		| GetDeviceDebugInfo400
		| void
		| GetDeviceDebugInfo404
		| GetDeviceDebugInfo500,
>(
	deviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getDeviceDebugInfo>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ?? getGetDeviceDebugInfoQueryKey(deviceId);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof getDeviceDebugInfo>>
	> = ({ signal }) =>
		getDeviceDebugInfo(deviceId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: deviceId !== null && deviceId !== undefined,
		...queryOptions,
	} as UseQueryOptions<
		Awaited<ReturnType<typeof getDeviceDebugInfo>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDeviceDebugInfoQueryResult = NonNullable<
	Awaited<ReturnType<typeof getDeviceDebugInfo>>
>;
export type GetDeviceDebugInfoQueryError =
	| GetDeviceDebugInfo400
	| void
	| GetDeviceDebugInfo404
	| GetDeviceDebugInfo500;

export function useGetDeviceDebugInfo<
	TData = Awaited<ReturnType<typeof getDeviceDebugInfo>>,
	TError =
		| GetDeviceDebugInfo400
		| void
		| GetDeviceDebugInfo404
		| GetDeviceDebugInfo500,
>(
	deviceId: string,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getDeviceDebugInfo>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getDeviceDebugInfo>>,
					TError,
					Awaited<ReturnType<typeof getDeviceDebugInfo>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDeviceDebugInfo<
	TData = Awaited<ReturnType<typeof getDeviceDebugInfo>>,
	TError =
		| GetDeviceDebugInfo400
		| void
		| GetDeviceDebugInfo404
		| GetDeviceDebugInfo500,
>(
	deviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getDeviceDebugInfo>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getDeviceDebugInfo>>,
					TError,
					Awaited<ReturnType<typeof getDeviceDebugInfo>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDeviceDebugInfo<
	TData = Awaited<ReturnType<typeof getDeviceDebugInfo>>,
	TError =
		| GetDeviceDebugInfo400
		| void
		| GetDeviceDebugInfo404
		| GetDeviceDebugInfo500,
>(
	deviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getDeviceDebugInfo>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get debug information for a device (admin only)
 */

export function useGetDeviceDebugInfo<
	TData = Awaited<ReturnType<typeof getDeviceDebugInfo>>,
	TError =
		| GetDeviceDebugInfo400
		| void
		| GetDeviceDebugInfo404
		| GetDeviceDebugInfo500,
>(
	deviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getDeviceDebugInfo>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetDeviceDebugInfoQueryOptions(deviceId, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get debug information for a device (admin only)
 */
export const useSetGetDeviceDebugInfoQueryData = () => {
	const queryClient = useQueryClient();
	return (
		deviceId: string,
		updater:
			| Awaited<ReturnType<typeof getDeviceDebugInfo>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getDeviceDebugInfo>> | undefined,
			  ) => Awaited<ReturnType<typeof getDeviceDebugInfo>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getDeviceDebugInfo>>>(
			{ exact: $exactMatch, queryKey: getGetDeviceDebugInfoQueryKey(deviceId) },
			updater,
		);
	};
};

/**
 * @summary Get debug information for a device (admin only)
 */
export const useGetGetDeviceDebugInfoQueryData = () => {
	const queryClient = useQueryClient();
	return (deviceId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getDeviceDebugInfo>>>(
			getGetDeviceDebugInfoQueryKey(deviceId),
		);
};

export const getRunDeviceNetworkTestUrl = (deviceId: string) => {
	return `/devices/${deviceId}/network-test`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Initiate a network test for a device (max 5 concurrent per user)
 */
export const runDeviceNetworkTest = async (
	deviceId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	return customFetch<Device>(getRunDeviceNetworkTestUrl(deviceId), {
		...options,
		method: "POST",
	});
};

export const getRunDeviceNetworkTestMutationKey = () =>
	["runDeviceNetworkTest"] as const;

export const getRunDeviceNetworkTestMutationOptions = <
	TError =
		| RunDeviceNetworkTest400
		| void
		| RunDeviceNetworkTest404
		| RunDeviceNetworkTest429
		| RunDeviceNetworkTest500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof runDeviceNetworkTest>>,
		TError,
		RunDeviceNetworkTestMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof runDeviceNetworkTest>>,
	TError,
	RunDeviceNetworkTestMutationVariables,
	TContext
> => {
	const mutationKey = getRunDeviceNetworkTestMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof runDeviceNetworkTest>>,
		RunDeviceNetworkTestMutationVariables
	> = (props) => {
		const { deviceId } = props ?? {};

		return runDeviceNetworkTest(deviceId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type RunDeviceNetworkTestMutationResult = NonNullable<
	Awaited<ReturnType<typeof runDeviceNetworkTest>>
>;

export type RunDeviceNetworkTestMutationError =
	| RunDeviceNetworkTest400
	| void
	| RunDeviceNetworkTest404
	| RunDeviceNetworkTest429
	| RunDeviceNetworkTest500;
export type RunDeviceNetworkTestMutationVariables = { deviceId: string };

/**
 * @summary Initiate a network test for a device (max 5 concurrent per user)
 */
export const useRunDeviceNetworkTest = <
	TError =
		| RunDeviceNetworkTest400
		| void
		| RunDeviceNetworkTest404
		| RunDeviceNetworkTest429
		| RunDeviceNetworkTest500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof runDeviceNetworkTest>>,
			TError,
			RunDeviceNetworkTestMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof runDeviceNetworkTest>>,
	TError,
	RunDeviceNetworkTestMutationVariables,
	TContext
> => {
	return useMutation(
		getRunDeviceNetworkTestMutationOptions(options),
		queryClient,
	);
};
export const getSetDeviceOperatingModeUrl = (deviceId: string) => {
	return `/devices/${deviceId}/operatingMode`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Set the remote manual override (valve open/closed/none) for a device
 */
export const setDeviceOperatingMode = async (
	deviceId: string,
	setDeviceOperatingModeBody: SetDeviceOperatingModeBody,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<Device>(getSetDeviceOperatingModeUrl(deviceId), {
		...options,
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(setDeviceOperatingModeBody),
	});
};

export const getSetDeviceOperatingModeMutationKey = () =>
	["setDeviceOperatingMode"] as const;

export const getSetDeviceOperatingModeMutationOptions = <
	TError =
		| SetDeviceOperatingMode400
		| void
		| SetDeviceOperatingMode404
		| SetDeviceOperatingMode500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof setDeviceOperatingMode>>,
		TError,
		SetDeviceOperatingModeMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof setDeviceOperatingMode>>,
	TError,
	SetDeviceOperatingModeMutationVariables,
	TContext
> => {
	const mutationKey = getSetDeviceOperatingModeMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof setDeviceOperatingMode>>,
		SetDeviceOperatingModeMutationVariables
	> = (props) => {
		const { deviceId, data } = props ?? {};

		return setDeviceOperatingMode(deviceId, data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type SetDeviceOperatingModeMutationResult = NonNullable<
	Awaited<ReturnType<typeof setDeviceOperatingMode>>
>;
export type SetDeviceOperatingModeMutationBody = SetDeviceOperatingModeBody;
export type SetDeviceOperatingModeMutationError =
	| SetDeviceOperatingMode400
	| void
	| SetDeviceOperatingMode404
	| SetDeviceOperatingMode500;
export type SetDeviceOperatingModeMutationVariables = {
	deviceId: string;
	data: SetDeviceOperatingModeBody;
};

/**
 * @summary Set the remote manual override (valve open/closed/none) for a device
 */
export const useSetDeviceOperatingMode = <
	TError =
		| SetDeviceOperatingMode400
		| void
		| SetDeviceOperatingMode404
		| SetDeviceOperatingMode500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof setDeviceOperatingMode>>,
			TError,
			SetDeviceOperatingModeMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof setDeviceOperatingMode>>,
	TError,
	SetDeviceOperatingModeMutationVariables,
	TContext
> => {
	return useMutation(
		getSetDeviceOperatingModeMutationOptions(options),
		queryClient,
	);
};
export const getClearDeviceWarningsUrl = (deviceId: string) => {
	return `/devices/${deviceId}/warnings`;
};

/**
 * **Access:** Requires a user session token. Administrators only.
 * @summary Clear all warnings for a device (admin only)
 */
export const clearDeviceWarnings = async (
	deviceId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	return customFetch<Device>(getClearDeviceWarningsUrl(deviceId), {
		...options,
		method: "DELETE",
	});
};

export const getClearDeviceWarningsMutationKey = () =>
	["clearDeviceWarnings"] as const;

export const getClearDeviceWarningsMutationOptions = <
	TError =
		| ClearDeviceWarnings400
		| void
		| ClearDeviceWarnings404
		| ClearDeviceWarnings500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof clearDeviceWarnings>>,
		TError,
		ClearDeviceWarningsMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof clearDeviceWarnings>>,
	TError,
	ClearDeviceWarningsMutationVariables,
	TContext
> => {
	const mutationKey = getClearDeviceWarningsMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof clearDeviceWarnings>>,
		ClearDeviceWarningsMutationVariables
	> = (props) => {
		const { deviceId } = props ?? {};

		return clearDeviceWarnings(deviceId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type ClearDeviceWarningsMutationResult = NonNullable<
	Awaited<ReturnType<typeof clearDeviceWarnings>>
>;

export type ClearDeviceWarningsMutationError =
	| ClearDeviceWarnings400
	| void
	| ClearDeviceWarnings404
	| ClearDeviceWarnings500;
export type ClearDeviceWarningsMutationVariables = { deviceId: string };

/**
 * @summary Clear all warnings for a device (admin only)
 */
export const useClearDeviceWarnings = <
	TError =
		| ClearDeviceWarnings400
		| void
		| ClearDeviceWarnings404
		| ClearDeviceWarnings500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof clearDeviceWarnings>>,
			TError,
			ClearDeviceWarningsMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof clearDeviceWarnings>>,
	TError,
	ClearDeviceWarningsMutationVariables,
	TContext
> => {
	return useMutation(
		getClearDeviceWarningsMutationOptions(options),
		queryClient,
	);
};
export const getClearDeviceVerificationUrl = (deviceId: string) => {
	return `/devices/${deviceId}/verification`;
};

/**
 * **Access:** Requires a user session token. Administrators only.
 * @summary Reset irrigation verification state for a device and re-run verification (admin only)
 */
export const clearDeviceVerification = async (
	deviceId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Device> => {
	return customFetch<Device>(getClearDeviceVerificationUrl(deviceId), {
		...options,
		method: "DELETE",
	});
};

export const getClearDeviceVerificationMutationKey = () =>
	["clearDeviceVerification"] as const;

export const getClearDeviceVerificationMutationOptions = <
	TError =
		| ClearDeviceVerification400
		| void
		| ClearDeviceVerification404
		| ClearDeviceVerification500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof clearDeviceVerification>>,
		TError,
		ClearDeviceVerificationMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof clearDeviceVerification>>,
	TError,
	ClearDeviceVerificationMutationVariables,
	TContext
> => {
	const mutationKey = getClearDeviceVerificationMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof clearDeviceVerification>>,
		ClearDeviceVerificationMutationVariables
	> = (props) => {
		const { deviceId } = props ?? {};

		return clearDeviceVerification(deviceId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type ClearDeviceVerificationMutationResult = NonNullable<
	Awaited<ReturnType<typeof clearDeviceVerification>>
>;

export type ClearDeviceVerificationMutationError =
	| ClearDeviceVerification400
	| void
	| ClearDeviceVerification404
	| ClearDeviceVerification500;
export type ClearDeviceVerificationMutationVariables = { deviceId: string };

/**
 * @summary Reset irrigation verification state for a device and re-run verification (admin only)
 */
export const useClearDeviceVerification = <
	TError =
		| ClearDeviceVerification400
		| void
		| ClearDeviceVerification404
		| ClearDeviceVerification500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof clearDeviceVerification>>,
			TError,
			ClearDeviceVerificationMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof clearDeviceVerification>>,
	TError,
	ClearDeviceVerificationMutationVariables,
	TContext
> => {
	return useMutation(
		getClearDeviceVerificationMutationOptions(options),
		queryClient,
	);
};
